export type AmazonSupportViolation = {
  type: string;
  evidence?: string;
};

export type AmazonSupportReview = {
  title: string | null;
  body: string;
  classification: {
    violations?: AmazonSupportViolation[] | null;
    rationale: string | null;
    takedownRequest?: string | null;
  } | null;
};

export type AmazonReportTextSource =
  | "stored_draft"
  | "deterministic_fallback";

export type ResolvedAmazonReportText = {
  text: string;
  source: AmazonReportTextSource;
};

type MessageCopy = {
  reason: string;
  request: string;
};

const ACTIONABLE_VIOLATION_TYPES = new Set([
  "seller_feedback_misplaced",
  "conflict_of_interest",
  "incentivized",
  "profanity_or_harassment",
  "privacy_violation",
  "off_topic",
  "duplicate_or_template",
  "manufacturer_misrepresentation",
  "competitor_review",
  "review_swap",
]);

function primaryActionableViolationType(
  review: AmazonSupportReview
): string | null {
  return (
    review.classification?.violations?.find((violation) =>
      ACTIONABLE_VIOLATION_TYPES.has(violation.type)
    )?.type ?? null
  );
}

function cleanText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function truncate(value: string, length: number): string {
  const cleaned = cleanText(value);
  return cleaned.length > length
    ? `${cleaned.slice(0, length - 3).trimEnd()}...`
    : cleaned;
}

function redactSensitiveText(value: string): string {
  return value
    .replace(
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      "[email removed]"
    )
    .replace(
      /\b((?:order|account|customer)\s*(?:id|number|no\.?|#))\s*[:#-]?\s*[a-z0-9][a-z0-9-]{5,}\b/gi,
      "$1 [identifier removed]"
    )
    .replace(/\b\d{3}-\d{7}-\d{7}\b/g, "[order number removed]")
    .replace(
      /\b\d{1,6}\s+(?:[A-Z0-9][A-Z0-9.'-]*\s+){0,5}(?:street|st|avenue|ave|road|rd|boulevard|blvd|lane|ln|drive|dr|court|ct|way|parkway|pkwy|place|pl|terrace|ter)\.?(?:\s*,?\s*(?:apt\.?|unit|suite|#)\s*[A-Z0-9-]+)?/gi,
      "[address removed]"
    )
    .replace(/\bP\.?\s*O\.?\s+Box\s+\d+[A-Z]?\b/gi, "[address removed]")
    .replace(
      /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/g,
      "[phone removed]"
    )
    .replace(
      /(?<![A-Z0-9])(?:\d[\s-]*){7,}\d(?![A-Z0-9])/g,
      "[number removed]"
    )
    .replace(/\b\d{8,}\b/g, "[number removed]");
}

function usableStoredModelDraft(value: string | null | undefined): string | null {
  let candidate = value?.trim() ?? "";
  if (!candidate) return null;

  const fenced = candidate.match(/^```[^\n]*\n([\s\S]*?)\n?```$/);
  if (fenced) candidate = fenced[1].trim();
  if (!candidate || /^```(?:[a-z0-9_-]+)?\s*```$/i.test(candidate)) {
    return null;
  }

  const normalized = cleanText(candidate).toLowerCase();
  if (
    /^(?:n\/?a|none|null|undefined|tbd|not applicable|no (?:takedown )?request(?: is)? (?:needed|required|available)|no policy violation(?: found)?)\.?$/.test(
      normalized
    )
  ) {
    return null;
  }
  if (
    candidate.length < 600 &&
    /^(?:(?:i(?:['’]?m| am)|we(?:['’]?re| are)) sorry\b|sorry\b|as an (?:ai|language model)\b|i (?:can(?:not|['’]?t)|won['’]?t) (?:help|assist|provide|comply)\b)/i.test(
      candidate.replace(
        /^(?:hello|hi)(?:\s+amazon(?:\s+brand\s+registry)?\s+support)?\s*[, :-]?\s*/i,
        ""
      )
    )
  ) {
    return null;
  }

  return redactSensitiveText(candidate);
}

function reviewText(review: AmazonSupportReview): string {
  const title = cleanText(review.title ?? "");
  const body = cleanText(review.body);
  if (title && body && title.toLowerCase() !== body.toLowerCase()) {
    return `${title}: ${body}`;
  }
  return body || title;
}

function evidenceForReview(
  category: string,
  review: AmazonSupportReview
): string {
  const source = reviewText(review);
  const violation =
    review.classification?.violations?.find(
      (item) => item.type === category
    ) ?? review.classification?.violations?.[0];
  const evidence = cleanText(violation?.evidence ?? "");

  // Classifier evidence is useful only when it is a literal excerpt. Fall back
  // to the review itself instead of showing an AI-written rationale as fact.
  if (
    evidence &&
    cleanText(source).toLowerCase().includes(evidence.toLowerCase())
  ) {
    return redactSensitiveText(truncate(evidence, 160));
  }
  return redactSensitiveText(
    truncate(source || "Review text unavailable", 160)
  );
}

function formatList(values: string[]): string {
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

function sellerFeedbackTopics(evidence: string[]): string {
  const text = evidence.join(" ").toLowerCase();
  const topics: string[] = [];

  if (
    /\b(missing|without|not attached|did not have|didn't have|broken|damaged|defective|incomplete|leak|leaked|leaking|dirty|opened)\b/.test(
      text
    )
  ) {
    topics.push("the condition of specific items received");
  }
  if (
    /\b(package|packaging|box|boxed|wrap|wrapped|wrapping|seal|sealed|cellophane|bag)\b/.test(
      text
    )
  ) {
    topics.push("packaging");
  }
  if (
    /\b(ship|shipping|shipped|shipment|delivery|delivered|carrier|fedex|ups|usps|tracking)\b/.test(
      text
    )
  ) {
    topics.push("shipping or delivery");
  }
  if (
    /\b(seller|order|refund|return|replacement|replace|customer service|support)\b/.test(
      text
    )
  ) {
    topics.push("an order or seller service");
  }

  return formatList(topics.length > 0 ? topics : ["an order-specific issue"]);
}

function messageCopy(
  category: string,
  reviewCount: number,
  evidence: string[]
): MessageCopy {
  const plural = reviewCount !== 1;
  const subject = plural ? "The reviews" : "The review";
  const object = plural ? "these reviews" : "this review";

  switch (category) {
    case "seller_feedback_misplaced":
      return {
        reason: `${subject} ${plural ? "focus" : "focuses"} on ${sellerFeedbackTopics(
          evidence
        )} rather than the product itself. That kind of issue is better handled as seller, order, or fulfillment feedback.`,
        request: `Please remove ${object} from the product page or move ${plural ? "them" : "it"} to the appropriate feedback area.`,
      };
    case "off_topic":
      return {
        reason: `${subject} ${plural ? "are" : "is"} about something other than the product shown on the listing.`,
        request: `Please remove ${object} from the product page.`,
      };
    case "competitor_review":
      return {
        reason: `${subject} ${plural ? "promote or recommend" : "promotes or recommends"} another product instead of reviewing the listed product.`,
        request: `Please remove ${object} from the product page.`,
      };
    case "incentivized":
      return {
        reason: `${subject} ${plural ? "mention" : "mentions"} a free product, discount, refund, or other benefit connected to the review.`,
        request: `Please review ${object} for an incentive and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
    case "conflict_of_interest":
    case "manufacturer_misrepresentation":
      return {
        reason: `${subject} ${plural ? "indicate" : "indicates"} a connection to the seller, brand, manufacturer, or product that calls the independence of the feedback into question.`,
        request: `Please review ${object} and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
    case "profanity_or_harassment":
      return {
        reason: `${subject} ${plural ? "contain" : "contains"} abusive, harassing, threatening, or profane language. The request is about that language, not the customer's opinion of the product.`,
        request: `Please review ${object} and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
    case "privacy_violation":
      return {
        reason: `${subject} ${plural ? "include" : "includes"} personal or identifying information in a public review.`,
        request: `Please remove ${object} from the product page.`,
      };
    case "duplicate_or_template":
      return {
        reason: `${subject} ${plural ? "use" : "uses"} duplicated or templated wording rather than original feedback.`,
        request: `Please review ${object} and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
    case "review_swap":
      return {
        reason: `${subject} ${plural ? "describe" : "describes"} reciprocal or coordinated review activity.`,
        request: `Please review ${object} and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
    default:
      return {
        reason: `The quoted wording below is the reason for this request. The request is not based on the rating or the fact that the feedback is critical.`,
        request: `Please review ${object} and remove ${plural ? "them" : "it"} if ${plural ? "they violate" : "it violates"} Amazon's review rules.`,
      };
  }
}

export function buildAmazonSupportMessage(
  category: string,
  reviews: AmazonSupportReview[]
): string {
  if (reviews.length === 0 || !ACTIONABLE_VIOLATION_TYPES.has(category)) {
    return "";
  }

  const count = reviews.length;
  const evidence = reviews.map((review) => evidenceForReview(category, review));
  const copy = messageCopy(category, count, evidence);
  const evidenceSection =
    category === "privacy_violation"
      ? "I have not repeated the personal information here because this form asks us not to include sensitive information."
      : count === 1
      ? `The review says: “${evidence[0]}”`
      : [
          "Relevant excerpts, in the same order as the review URLs:",
          ...evidence.map((item, index) => `${index + 1}. “${item}”`),
        ].join("\n");

  return [
    "Hello Amazon Brand Registry Support,",
    "",
    `Could you please review ${
      count === 1 ? "the customer review" : `the ${count} customer reviews`
    } listed in this request?`,
    "",
    copy.reason,
    "",
    evidenceSection,
    "",
    copy.request,
    "",
    "Thank you for your help.",
  ].join("\n");
}

export function resolveAmazonReportText(
  review: AmazonSupportReview
): ResolvedAmazonReportText {
  const category = primaryActionableViolationType(review);
  if (!category) {
    return { text: "", source: "deterministic_fallback" };
  }

  const storedDraft = usableStoredModelDraft(
    review.classification?.takedownRequest
  );
  if (storedDraft) {
    return { text: storedDraft, source: "stored_draft" };
  }

  return {
    text: buildAmazonSupportMessage(category, [review]),
    source: "deterministic_fallback",
  };
}
